import { QueryResponse } from "@/shared/types/base";
import { useMemo, useRef } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { useFiltersReturn } from "@/components/filters";

export interface usePaginationProps {
    filterData?: useFiltersReturn,
    queryResult?: UseQueryResult<QueryResponse<any>>
}

export interface usePaginationReturn {
    page: number,
    pageSize: number,
    count: number,
    setPagination: (page: number, pageSize?: number) => void,
}

export default function usePagination({
    filterData,
    queryResult
}: usePaginationProps): usePaginationReturn {
    const queryResultRef = useRef(queryResult);
    const lastValidQueryResult = useMemo(() => {
        if (queryResult && queryResult.data) {
            queryResultRef.current = queryResult;
            return queryResult;
        } else {
            return queryResultRef.current;
        }
    }, [queryResult]);

    return useMemo(() => {
        const result = { page: -1, pageSize: -1, count: -1, setPagination: () => { } } as usePaginationReturn;

        if (filterData) {
            const pagination = filterData.filters.find(filter => filter.type === "pagination");
            if (pagination) {
                result.page = pagination.page;
                result.pageSize = pagination.pageSize;
                result.setPagination = (page: number, pageSize?: number) => {
                    const prevFilterData = filterData.filterData;
                    filterData.setFilterData({
                        ...prevFilterData,
                        [pagination.pageName]: page,
                        [pagination.pageSizeName]: pageSize || pagination.pageSize
                    })
                }
            }
        }

        if (lastValidQueryResult && lastValidQueryResult.data) {
            result.count = lastValidQueryResult.data.count;
        }

        if (queryResult && queryResult.data) {
            result.page = queryResult.data.page;
            result.pageSize = queryResult.data.pageSize;
            result.count = queryResult.data.count;
        }

        result.page = clampPage(result.page, result.pageSize, result.count);
        result.pageSize = result.pageSize <= 0 ? -9999 : result.pageSize;
        result.count = result.count < 0 ? -9999 : result.count;
        
        return result;
    }, [filterData, lastValidQueryResult, queryResult]);
}

const clampPage = (page: number, pageSize: number, count: number) => {
    const getPageRange = (pageSize: number, count: number): [number, number] => {
        const integerDivision = (a: number, b: number) => {
            return [Math.floor(a / b), a % b];
        }

        if (pageSize <= 0 || count <= 0) return [0, 0];
        const [q, r] = integerDivision(count, pageSize);
        const pageMin = 0;
        const pageMax = q + Number(r !== 0) - 1;
        return [pageMin, pageMax];
    }

    const [min, max] = getPageRange(pageSize, count);
    return Math.min(Math.max(page, min), max);
}